/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnInit, Component, Input } from '@angular/core';
import { Validators } from '@angular/forms';
import { type FormArray, type SelectOption, FormBuilder, FormControl, FormGroup } from '@dm/core';
import { INFINITE_NUM_64, INVALID_NUM } from 'app/common/constants';
import { ScheduleTypeE } from 'app/common/constants/hyper-cdp.const';
import { ToolService } from 'app/common/services/tool.service';
import { HyperCdpSchedule } from 'app/common/types/mo';
import { isEqual } from 'lodash';

import {
    CDP_S_POLICY_FIXEDTIME_HOUR_RATE, CDP_S_POLICY_FIXEDTIME_MIN_RATE, CDP_S_POLICY_TIME_INITIAL, FrequenciesE,
    HYPER_CDP_CONFIG_POLICY_S_TIME_FORM_ITEM_LIST, HyperCdpConfigPolicySDailyMinuteValue, HyperCdpConfigPolicySHourValue,
    HyperCdpConfigPolicySMinuteValue, HyperCdpConfigPolicySNumberRange, HyperCdpConfigPolicySRetainValue,
    HyperCdpConfigPolicySSecondValue, MAX_VALUE,
} from '../config-policy.const';
import { ConfigPolicyStrService, ScheduleNumE } from '../config-policy-str.service';
import { type HyperCdpConfigPolicySVmValue } from './config-policy-s.d';

@Component({
    selector: 'app-hyper-cdp-schedule-create-config-policy-s',
    templateUrl: './config-policy-s.component.html',
    styleUrls: ['./config-policy-s.component.scss'],
})

export class ConfigPolicySComponent implements OnInit {
    @Input() data: HyperCdpSchedule;
    @Input() type: ScheduleTypeE = ScheduleTypeE.Block;

    // 表单相关配置
    newExecutionCycleVm: FormGroup;
    executionCycleCtrs: FormArray;
    newExecutionCycleErrorMessage: { [field: string]: { [error: string]: any } };

    // 常量导入
    fixedSecond = HyperCdpConfigPolicySSecondValue.FILE_INITIAL;
    minSecond = HyperCdpConfigPolicySSecondValue.BLOCK_MIN;
    minuteMin = HyperCdpConfigPolicySMinuteValue.INITIAL;
    FrequenciesE = FrequenciesE;
    HyperCdpConfigPolicySSecondValue = HyperCdpConfigPolicySSecondValue;
    HyperCdpConfigPolicySMinuteValue = HyperCdpConfigPolicySMinuteValue;
    HyperCdpConfigPolicySHourValue = HyperCdpConfigPolicySHourValue;
    HyperCdpConfigPolicySDailyMinuteValue = HyperCdpConfigPolicySDailyMinuteValue;
    HyperCdpConfigPolicySRetainValue = HyperCdpConfigPolicySRetainValue;

    // 选项
    timeUnitOpts: SelectOption[] = this.str.timeUnitOpts;
    mouthlyDayOpt: SelectOption[] = this.str.mouthlyDayOption();
    hourOpt: SelectOption[] = this.str.dailyHourOption();
    dateOpt: SelectOption[] = this.str.dateOpt;

    /**
     * 是否配置过少策略（至少配置一条策略）
     */
    isLessThanExecutionCycle = false;

    /**
     * 是否禁用添加策略按钮（最多配置四条策略,边界值四条时按钮灰化，不允许用户添加）
     */
    isAddExecutionCycleBtnDisabled = false;

    /**
     * 是否可以下发策略参数（最多配置四条策略,边界值四条时可以下发，超过四条不允许下发）
     */
    isBuildExecutionCycleCmdAbled = false;

    /**
     * 是否为安全快照
     */
    isSecureSnapenAbled = false;

    /**
     * 固定周期数组（包含时分秒）
     */
    fixedPeriodArr = [FrequenciesE.SECONDLY, FrequenciesE.MINUTELY, FrequenciesE.HOURLY];

    initScheduleNum: number;

    initScheduleData: HyperCdpSchedule;

    i18n = this.str;

    constructor(
        public str: ConfigPolicyStrService,
        private fb: FormBuilder,
        private toolService: ToolService,
        private tool: ToolService,
    ) { }

    ngOnInit(): void {
        this.changeItemInitialValue();
        this.buildVm();
        this.patchModifyValue();
    }

    /**
     * 根据CDP类型获取默认值
     */
    changeItemInitialValue(): void {
        this.minSecond = this.type === ScheduleTypeE.File ?
            HyperCdpConfigPolicySSecondValue.FILE_MIN : HyperCdpConfigPolicySSecondValue.BLOCK_MIN;
        this.fixedSecond = this.type === ScheduleTypeE.File ?
            HyperCdpConfigPolicySSecondValue.FILE_INITIAL : HyperCdpConfigPolicySSecondValue.BLOCK_INITIAL;
        this.isSecureSnapenAbled = this.data?.SECURESNAPENABLED === 'true';
        this.initScheduleNum = this.data ? this.getScheduleNum(this.data) : ScheduleNumE.DefaultNum;
    }

    /**
     * 修改时传递data
     */
    patchModifyValue(): void {
        if (!this.data) {
            return;
        }

        const modifyData = this.data;

        // 插入表单项之前先清空初始化表单时的默认项
        this.executionCycleCtrs.removeAt(0);

        // 含固定周期时
        if (!this.tool.isInvalidNum(modifyData.FREQUENCYVALUE)) {
            const timeValue = Number(modifyData.FREQUENCYVALUE);
            const patchValue: HyperCdpConfigPolicySVmValue = {
                executionCycleWay: '',
                number: HyperCdpConfigPolicySRetainValue.INITIAL,
            };

            patchValue.number = Number(this.data.FREQUENCYNUM);

            if (timeValue % CDP_S_POLICY_FIXEDTIME_HOUR_RATE === 0) {
                patchValue.executionCycleWay = FrequenciesE.HOURLY;
                patchValue.hour = timeValue / CDP_S_POLICY_FIXEDTIME_HOUR_RATE;
            } else if (timeValue % CDP_S_POLICY_FIXEDTIME_MIN_RATE === 0) {
                patchValue.executionCycleWay = FrequenciesE.MINUTELY;
                patchValue.minute = timeValue / CDP_S_POLICY_FIXEDTIME_MIN_RATE;
            } else {
                patchValue.executionCycleWay = FrequenciesE.SECONDLY;
                patchValue.second = timeValue;
            }

            this.executionCycleCtrs.push(this.createNewLunForm(patchValue));
        }

        // 含每天
        if (!this.tool.isInvalidNum(modifyData.DAYMINUTE)) {
            const patchValue: HyperCdpConfigPolicySVmValue = {
                executionCycleWay: FrequenciesE.DAILY,
                number: Number(modifyData.DAILYSNAPSHOTNUM),
            };

            patchValue.dailyHour = JSON.parse(modifyData.DAYHOURS as any as string);
            patchValue.dailyMinute = Number(modifyData.DAYMINUTE);

            this.executionCycleCtrs.push(this.createNewLunForm(patchValue));
        }

        // 含每周
        if (modifyData.STARTTIMEOFWEEK) {
            const patchValue: HyperCdpConfigPolicySVmValue = {
                executionCycleWay: FrequenciesE.WEEKLY,
                number: Number(modifyData.WEEKLYSNAPSHOTNUM),
            };

            patchValue.executionCycleWay = FrequenciesE.WEEKLY;
            patchValue.weeklyDay = JSON.parse(modifyData.WEEKLYDAYS as any as string);
            patchValue.weeklyTime = modifyData.STARTTIMEOFWEEK;

            this.executionCycleCtrs.push(this.createNewLunForm(patchValue));
        }

        // 含每月
        if (modifyData.STARTTIMEOFMONTH) {
            const patchValue: HyperCdpConfigPolicySVmValue = {
                executionCycleWay: FrequenciesE.MONTHLY,
                number: Number(modifyData.MONTHSNAPSHOTNUM),
            };

            patchValue.mouthlyDay = JSON.parse(modifyData.MONTHDAYS as any as string);
            patchValue.mouthlyTime = modifyData.STARTTIMEOFMONTH;

            this.executionCycleCtrs.push(this.createNewLunForm(patchValue));
        }

        this.initScheduleData = this.getPolicyConfig();
        this.controlFormAbled();
        this.isExecutionCycleNumberValidate();
    }

    /**
     * 处理全表单Abled
     */
    controlFormAbled(): void {
        this.executionCycleCtrs.controls.forEach((item: FormGroup, formItemIndex: number) => {
            const executionCycleWay = item.get('executionCycleWay').value;
            this.controlFormItemAbled(executionCycleWay, formItemIndex);
        });
    }

    /**
     * 初始化表单
     */
    buildVm(): void {
        const initArray = [this.createNewLunForm()];
        this.newExecutionCycleVm = this.fb.group({
            newExecutionCycles: this.fb.array(initArray),
        });

        this.executionCycleCtrs = this.newExecutionCycleVm.get('newExecutionCycles') as FormArray;

        this.newExecutionCycleErrorMessage = {
            executionCycleWay: {
                required: this.toolService.requiredErr(this.i18n.title.timePolicyLab),
            },
            dailyHour: {
                required: this.toolService.requiredErr(this.i18n.title.hour),
            },
            weeklyDay: {
                required: this.toolService.requiredErr(this.i18n.title.week),
            },
            mouthlyDay: {
                required: this.toolService.requiredErr(this.i18n.title.date),
            },
        };
    }

    /**
     * 增加执行周期
     */
    addNewExecutionCycle(): void {
        this.executionCycleCtrs.push(this.createNewLunForm());
        this.isExecutionCycleNumberValidate();
    }

    /**
     * 移除执行周期
     */
    deleteNewExecutionCycle(index: number): void {
        this.executionCycleCtrs.removeAt(index);
        this.isExecutionCycleNumberValidate();
    }

    /**
     * 创建新执行周期表单对象
     */
    createNewLunForm(patchValue?: HyperCdpConfigPolicySVmValue): FormGroup {
        const executionCyclegroup = new FormGroup({
            executionCycleWay: new FormControl(
                patchValue?.executionCycleWay || '', {
                validators: [
                    Validators.required,
                ],
            }),
            second: new FormControl(
                patchValue?.second || this.fixedSecond, {
                validators: [
                    Validators.required,
                ],
            }),
            minute: new FormControl(
                patchValue?.minute || HyperCdpConfigPolicySMinuteValue.INITIAL),
            hour: new FormControl(
                patchValue?.hour || HyperCdpConfigPolicySHourValue.INITIAL),
            dailyHour: new FormControl(
                patchValue?.dailyHour ?? [], {
                validators: [
                    Validators.required,
                ],
            }),
            dailyMinute: new FormControl(
                patchValue?.dailyMinute || HyperCdpConfigPolicySDailyMinuteValue.INITIAL),
            weeklyDay: new FormControl(
                patchValue?.weeklyDay ?? [], {
                validators: [
                    Validators.required,
                ],
            }),
            weeklyTime: new FormControl(
                patchValue?.weeklyTime || CDP_S_POLICY_TIME_INITIAL),
            mouthlyDay: new FormControl(
                patchValue?.mouthlyDay ?? [], {
                validators: [
                    Validators.required,
                ],
            }),
            mouthlyTime: new FormControl(
                patchValue?.mouthlyTime || CDP_S_POLICY_TIME_INITIAL),
            number: new FormControl(
                // 设置了安全快照数量/取消过某策略时，底层会返回-1，此时应该取默认值1
                patchValue?.number && patchValue?.number !== HyperCdpConfigPolicySRetainValue.CANCEL_SECURE_SNAPEN ?
                    patchValue?.number : HyperCdpConfigPolicySRetainValue.INITIAL),
        }, { updateOn: 'blur' });

        return executionCyclegroup;
    }

    /**
     * 执行周期改变
     */
    onExecutionCycleWayChange(event: { value: FrequenciesE }, formItemIndex: number): void {
        const executionCycleWay = event.value;
        this.controlFormItemAbled(executionCycleWay, formItemIndex);
        this.setNumberMaxValue(executionCycleWay, formItemIndex);
    }

    /**
     * 控制表单项是否可用
     */
    controlFormItemAbled(executionCycleWay: FrequenciesE, formItemIndex: number): void {
        HYPER_CDP_CONFIG_POLICY_S_TIME_FORM_ITEM_LIST.forEach((itemName: string) => {
            this.executionCycleCtrs.controls[formItemIndex].get(itemName).disable();
        });
        switch (executionCycleWay) {
            case FrequenciesE.SECONDLY:
                this.executionCycleCtrs.controls[formItemIndex].get('second').enable();
                break;
            case FrequenciesE.MINUTELY:
                this.executionCycleCtrs.controls[formItemIndex].get('minute').enable();
                break;
            case FrequenciesE.HOURLY:
                this.executionCycleCtrs.controls[formItemIndex].get('hour').enable();
                break;
            case FrequenciesE.DAILY:
                this.executionCycleCtrs.controls[formItemIndex].get('dailyHour').enable();
                this.executionCycleCtrs.controls[formItemIndex].get('dailyMinute').enable();
                break;
            case FrequenciesE.WEEKLY:
                this.executionCycleCtrs.controls[formItemIndex].get('weeklyDay').enable();
                this.executionCycleCtrs.controls[formItemIndex].get('weeklyTime').enable();
                break;
            case FrequenciesE.MONTHLY:
                this.executionCycleCtrs.controls[formItemIndex].get('mouthlyDay').enable();
                this.executionCycleCtrs.controls[formItemIndex].get('mouthlyTime').enable();
                break;
            // no default
        }

        if (this.isSecureSnapenAbled) {
            this.executionCycleCtrs.controls[formItemIndex].get('number').disable();
        } else {
            this.executionCycleCtrs.controls[formItemIndex].get('number').enable();
        }

    }

    /**
     * 校验执行周期策略数量
     */
    isExecutionCycleNumberValidate(): void {
        const policyNum = this.executionCycleCtrs.controls.length;
        this.isAddExecutionCycleBtnDisabled = policyNum >= HyperCdpConfigPolicySNumberRange.MAX;
        this.isLessThanExecutionCycle = policyNum < HyperCdpConfigPolicySNumberRange.MIN;
        this.isBuildExecutionCycleCmdAbled = policyNum > HyperCdpConfigPolicySNumberRange.MAX;
    }

    /**
     * 修改周期选项（打开周期选择框时）
     */
    changeTimeUnitOpts(formItemIndex: number): void {
        // 固定周期（时分秒）禁用条件：选择了时分秒任一选项且目前该选项不为时分秒任一选项
        const isDisabledFixPeriod = this.isFixedPeriodItemDisabled(formItemIndex);
        this.timeUnitOpts = [
            {
                label: this.i18n.title.second,
                value: FrequenciesE.SECONDLY,
                disabled: isDisabledFixPeriod,
            },
            {
                label: this.i18n.title.minute,
                value: FrequenciesE.MINUTELY,
                disabled: isDisabledFixPeriod,
            },
            {
                label: this.i18n.title.hour,
                value: FrequenciesE.HOURLY,
                disabled: isDisabledFixPeriod,
            },
            {
                label: this.i18n.title.day,
                value: FrequenciesE.DAILY,
                // 天禁用条件：选择了天且目前该选项不为天（周/月同理）
                disabled: this.isTagPeriodItemDisabled(formItemIndex, FrequenciesE.DAILY),
            },
            {
                label: this.i18n.title.weeklyLab,
                value: FrequenciesE.WEEKLY,
                disabled: this.isTagPeriodItemDisabled(formItemIndex, FrequenciesE.WEEKLY),
            },
            {
                label: this.i18n.title.mouthlyLab,
                value: FrequenciesE.MONTHLY,
                disabled: this.isTagPeriodItemDisabled(formItemIndex, FrequenciesE.MONTHLY),
            },
        ];
    }

    /**
     * 判断表单是否禁选固定周期（时分秒中的某一项）
     */
    isFixedPeriodItemDisabled(formItemIndex: number): boolean {
        const periodValue = this.executionCycleCtrs.controls[formItemIndex].value.executionCycleWay;

        const isFixedPeriodItem = this.fixedPeriodArr.includes(periodValue);
        const isAddFixedPeriod = this.executionCycleCtrs.controls.some((item: FormGroup) =>
            this.fixedPeriodArr.includes(item.value.executionCycleWay),
        );

        return isAddFixedPeriod && !isFixedPeriodItem;
    }

    /**
     * 判断表单项是否该禁用某一周期
     */
    isTagPeriodItemDisabled(formItemIndex: number, frequenciesTypeE: FrequenciesE): boolean {
        const isTagPeriodItem = this.executionCycleCtrs.controls[formItemIndex].value.executionCycleWay === frequenciesTypeE;
        const isAddTagPeriod =
            this.executionCycleCtrs.controls.some((item: FormGroup) => item.value.executionCycleWay === frequenciesTypeE);

        return !isTagPeriodItem && isAddTagPeriod;
    }

    /**
     * 获取下发参数
     */
    getPolicyConfig(): HyperCdpSchedule {
        const data: HyperCdpSchedule = {};

        this.executionCycleCtrs.controls.forEach((item: FormGroup) => {
            const executionCycleWay = item.get('executionCycleWay').value;

            if (!executionCycleWay) {
                return;
            }

            switch (executionCycleWay) {
                case FrequenciesE.SECONDLY:
                    data.FREQUENCYNUM = item.get('number').value;
                    data.FREQUENCYVALUE = item.get('second').value;
                    break;
                case FrequenciesE.MINUTELY:
                    data.FREQUENCYNUM = item.get('number').value;
                    data.FREQUENCYVALUE = String(item.get('minute').value * CDP_S_POLICY_FIXEDTIME_MIN_RATE);
                    break;
                case FrequenciesE.HOURLY:
                    data.FREQUENCYNUM = item.get('number').value;
                    data.FREQUENCYVALUE = String(item.get('hour').value * CDP_S_POLICY_FIXEDTIME_HOUR_RATE);
                    break;
                case FrequenciesE.DAILY:
                    data.DAILYSNAPSHOTNUM = item.get('number').value;
                    data.DAYMINUTE = item.get('dailyMinute').value;
                    data.DAYHOURS = item.get('dailyHour').value;

                    // 数组数组排序，方便后续比较是否有下发参数改动
                    data.DAYHOURS.sort((a: number, b: number) => a - b);
                    break;
                case FrequenciesE.WEEKLY:
                    data.WEEKLYSNAPSHOTNUM = item.get('number').value;
                    data.WEEKLYDAYS = item.get('weeklyDay').value;
                    data.STARTTIMEOFWEEK = item.get('weeklyTime').value;
                    data.WEEKLYDAYS.sort((a: number, b: number) => a - b);
                    break;
                case FrequenciesE.MONTHLY:
                    data.MONTHSNAPSHOTNUM = item.get('number').value;
                    data.MONTHDAYS = item.get('mouthlyDay').value;
                    data.STARTTIMEOFMONTH = item.get('mouthlyTime').value;
                    data.MONTHDAYS.sort((a: number, b: number) => a - b);
                    break;
                // no default
            }
        });

        return data;
    }

    /**
     * 比较是否超过最大留存个数总和
     */
    exceedTheLimitNum(reservedNum: number): boolean {
        const curPolicyConfig = this.getPolicyConfig();
        const curScheduleNum = this.getScheduleNum(curPolicyConfig);
        return (reservedNum + curScheduleNum - this.initScheduleNum) > ScheduleNumE.MaxNum;
    }

    /**
     * 获取当前页HyperCDP计划保留个数
     */
    getScheduleNum(policyConfig: HyperCdpSchedule): number {
        // 安全快照不涉及留存个数
        if (this.isSecureSnapenAbled) {
            return 0;
        }

        let { FREQUENCYNUM = ScheduleNumE.DefaultNum,
            DAILYSNAPSHOTNUM = ScheduleNumE.DefaultNum,
            WEEKLYSNAPSHOTNUM = ScheduleNumE.DefaultNum,
            MONTHSNAPSHOTNUM = ScheduleNumE.DefaultNum } =
            policyConfig;

        // 修改界面的初始值，保留个数可能为-1
        FREQUENCYNUM = Number(FREQUENCYNUM) > 0 ? Number(FREQUENCYNUM) : 0;
        DAILYSNAPSHOTNUM = Number(DAILYSNAPSHOTNUM) > 0 ? Number(DAILYSNAPSHOTNUM) : 0;
        WEEKLYSNAPSHOTNUM = Number(WEEKLYSNAPSHOTNUM) > 0 ? Number(WEEKLYSNAPSHOTNUM) : 0;
        MONTHSNAPSHOTNUM = Number(MONTHSNAPSHOTNUM) > 0 ? Number(MONTHSNAPSHOTNUM) : 0;

        return FREQUENCYNUM + DAILYSNAPSHOTNUM + WEEKLYSNAPSHOTNUM + MONTHSNAPSHOTNUM;
    }

    /**
     * 获取修改的策略参数
     */
    getChangedPolifyConfig(): HyperCdpSchedule {
        const data: HyperCdpSchedule = this.getPolicyConfig();
        const changedData = this.getChangedValues();

        if (!changedData) {
            return {};
        }

        // 如果标准模式配置了安全快照，极简模式下文件系统CDP修改要下发安全快照相关参数,不支持修改，使用原data
        if (this.type === ScheduleTypeE.File) {
            if (!this.data) {
                return {};
            }

            // 含固定周期时，文件系统CDP需要填入固定周期是否开启安全周期，其他周期同理
            // isInvalidNum判断undefined返回false，未配置该参数时为undefined
            if (!this.tool.isInvalidNum(data.FREQUENCYVALUE) && data.FREQUENCYVALUE !== undefined) {
                data.FREQUENCYSECURESNAPENABLED = this.data.FREQUENCYSECURESNAPENABLED;
            }

            // 含每天
            if (!this.tool.isInvalidNum(data.DAYMINUTE) && data.DAYMINUTE !== undefined) {
                data.DAYSECURESNAPENABLED = this.data.DAYSECURESNAPENABLED;
            }

            // 含每周
            if (data.STARTTIMEOFWEEK) {
                data.WEEKLYSECURESNAPENABLED = this.data.WEEKLYSECURESNAPENABLED;
            }

            // 含每月
            if (data.STARTTIMEOFMONTH) {
                data.MONTHSECURESNAPENABLED = this.data.MONTHSECURESNAPENABLED;
            }

            // 固定周期设置了安全快照时，需要填入相关参数，其他周期同理
            if (this.data.FREQUENCYSECURESNAPENABLED) {
                data.FREQUENCYPROTECTPERIOD = this.data.FREQUENCYPROTECTPERIOD;
                data.FREQUENCYPERIODUNIT = this.data.FREQUENCYPERIODUNIT;
                data.FREQUENCYAUTODELETEENABLED = this.data.FREQUENCYAUTODELETEENABLED;
            }

            // 每天设置了安全快照
            if (this.data.DAYSECURESNAPENABLED) {
                data.DAYPROTECTIONPERIOD = this.data.DAYPROTECTIONPERIOD;
                data.DAYPERIODUNIT = this.data.DAYPERIODUNIT;
                data.DAYAUTODELETEENABLED = this.data.DAYAUTODELETEENABLED;
            }

            // 每周设置了安全快照
            if (this.data.WEEKLYSECURESNAPENABLED) {
                data.WEEKLYPROTECTIONPERIOD = this.data.WEEKLYPROTECTIONPERIOD;
                data.WEEKLYPERIODUNIT = this.data.WEEKLYPERIODUNIT;
                data.WEEKLYAUTODELETEENABLED = this.data.WEEKLYAUTODELETEENABLED;
            }

            // 每月设置了安全快照
            if (this.data.MONTHSECURESNAPENABLED) {
                data.MONTHPROTECTIONPERIOD = this.data.MONTHPROTECTIONPERIOD;
                data.MONTHPERIODUNIT = this.data.MONTHPERIODUNIT;
                data.MONTHAUTODELETEENABLED = this.data.MONTHAUTODELETEENABLED;
            }
        }

        // 是否取消某个周期策略（取消后相关参数置为初始值）
        const isFixedCancle = changedData.FREQUENCYVALUE === undefined && Object.hasOwnProperty.call(changedData, 'FREQUENCYVALUE');
        const isDaylyCancle = changedData.DAYMINUTE === undefined && Object.hasOwnProperty.call(changedData, 'DAYMINUTE');
        const isWeeklyCancle = changedData.STARTTIMEOFWEEK === undefined && Object.hasOwnProperty.call(changedData, 'STARTTIMEOFWEEK');
        const isMonthlyCancle = changedData.STARTTIMEOFMONTH === undefined && Object.hasOwnProperty.call(changedData, 'STARTTIMEOFMONTH');

        // 取消了固定周期，时分秒
        if (isFixedCancle) {
            data.FREQUENCYVALUE = String(INVALID_NUM);
            data.FREQUENCYNUM = String(INVALID_NUM);

            // 取消固定周期时，文件系统CDP需要置相关参数为初始值（其他周期同理）
            if (this.type === ScheduleTypeE.File) {
                data.FREQUENCYSECURESNAPENABLED = false;
                data.FREQUENCYAUTODELETEENABLED = false;
                data.FREQUENCYPROTECTPERIOD = INFINITE_NUM_64;
                data.FREQUENCYPERIODUNIT = String(INVALID_NUM);
                data.FREQUENCYTAG = '';
            }
        }

        // 取消了天
        if (isDaylyCancle) {
            data.DAYHOURS = [];
            data.DAYMINUTE = String(INVALID_NUM);
            data.DAILYSNAPSHOTNUM = String(INVALID_NUM);

            if (this.type === ScheduleTypeE.File) {
                data.DAYSECURESNAPENABLED = false;
                data.DAYAUTODELETEENABLED = false;
                data.DAYPROTECTIONPERIOD = INFINITE_NUM_64;
                data.DAYPERIODUNIT = String(INVALID_NUM);
                data.DAYTAG = '';
            }
        }

        // 取消了周
        if (isWeeklyCancle) {
            data.WEEKLYDAYS = [];
            data.STARTTIMEOFWEEK = '';
            data.WEEKLYSNAPSHOTNUM = String(INVALID_NUM);

            if (this.type === ScheduleTypeE.File) {
                data.WEEKLYSECURESNAPENABLED = false;
                data.WEEKLYAUTODELETEENABLED = false;
                data.WEEKLYPROTECTIONPERIOD = INFINITE_NUM_64;
                data.WEEKLYPERIODUNIT = String(INVALID_NUM);
                data.WEEKLYTAG = '';
            }
        }

        // 取消了月
        if (isMonthlyCancle) {
            data.MONTHDAYS = [];
            data.STARTTIMEOFMONTH = '';
            data.MONTHSNAPSHOTNUM = String(INVALID_NUM);

            if (this.type === ScheduleTypeE.File) {
                data.MONTHSECURESNAPENABLED = false;
                data.MONTHAUTODELETEENABLED = false;
                data.MONTHPROTECTIONPERIOD = INFINITE_NUM_64;
                data.MONTHPERIODUNIT = String(INVALID_NUM);
                data.MONTHTAG = '';
            }
        }

        // 安全快照不下发留存个数相关参数
        if (this.isSecureSnapenAbled) {
            delete data.FREQUENCYNUM;
            delete data.DAILYSNAPSHOTNUM;
            delete data.WEEKLYSNAPSHOTNUM;
            delete data.MONTHSNAPSHOTNUM;
        }

        if (!isFixedCancle && this.data.FREQUENCYSECURESNAPENABLED) {
            delete data.FREQUENCYNUM;
        }

        if (!isDaylyCancle && this.data.DAYSECURESNAPENABLED) {
            delete data.DAILYSNAPSHOTNUM;
        }

        if (!isWeeklyCancle && this.data.WEEKLYSECURESNAPENABLED) {
            delete data.WEEKLYSNAPSHOTNUM;
        }

        if (!isMonthlyCancle && this.data.MONTHSECURESNAPENABLED) {
            delete data.MONTHSNAPSHOTNUM;
        }

        return data;
    }

    /**
     * 比较两个对象的是否有不同，有则返回不同的对象合集，没有返回false
     */
    getChangedValues(): HyperCdpSchedule {
        const changedObj = {};
        const newData = this.getPolicyConfig();
        const HyperCdpConfigPolicySParams = {
            FREQUENCYNUM: '',
            FREQUENCYVALUE: '',
            DAILYSNAPSHOTNUM: '',
            DAYMINUTE: '',
            DAYHOURS: '',
            WEEKLYSNAPSHOTNUM: '',
            WEEKLYDAYS: '',
            STARTTIMEOFWEEK: '',
            MONTHSNAPSHOTNUM: '',
            MONTHDAYS: '',
            STARTTIMEOFMONTH: '',
        };

        for (const param in HyperCdpConfigPolicySParams) {
            if (!isEqual(this.initScheduleData[param], newData[param])) {
                changedObj[param] = newData[param];
            }
        }

        return changedObj;
    }

    /**
     * 校验，包括表单项校验和数量校验
     */
    executionCycleValidate(): boolean {
        this.isExecutionCycleNumberValidate();
        this.newExecutionCycleVm.validate();

        return this.newExecutionCycleVm.valid && !this.isBuildExecutionCycleCmdAbled && !this.isLessThanExecutionCycle;
    }

    /**
     * 是否需要留存个数项，安全快照不需要
     */
    isNeedNumber = (executionCycleWay: FrequenciesE): boolean => {
        if (!this.data) {
            return true;
        }

        if (this.isSecureSnapenAbled) {
            return false;
        }

        switch (executionCycleWay) {
            case FrequenciesE.DAILY:
                return !this.data.DAYSECURESNAPENABLED;
            case FrequenciesE.WEEKLY:
                return !this.data.WEEKLYSECURESNAPENABLED;
            case FrequenciesE.MONTHLY:
                return !this.data.MONTHSECURESNAPENABLED;
            default:
                return !this.data.FREQUENCYSECURESNAPENABLED;
        }
    };

    /**
     * 留存个数最大值
     */
    numberMaxValue = (executionCycleWay: FrequenciesE): number => {
        switch (executionCycleWay) {
            case FrequenciesE.DAILY:
                return this.type === ScheduleTypeE.File ? MAX_VALUE[ScheduleTypeE.File].DAILY : MAX_VALUE[ScheduleTypeE.Block].DAILY;
            case FrequenciesE.WEEKLY:
                return this.type === ScheduleTypeE.File ? MAX_VALUE[ScheduleTypeE.File].WEEKLY : MAX_VALUE[ScheduleTypeE.Block].WEEKLY;
            case FrequenciesE.MONTHLY:
                return this.type === ScheduleTypeE.File ? MAX_VALUE[ScheduleTypeE.File].MONTHLY : MAX_VALUE[ScheduleTypeE.Block].MONTHLY;
            default:
                return this.type === ScheduleTypeE.File ? MAX_VALUE[ScheduleTypeE.File].FIXED : MAX_VALUE[ScheduleTypeE.Block].FIXED;
        }
    };

    /**
     * 周期改变，number范围超出最大值时，赋值最大值；最小值均为1不考虑该情况
     */
    setNumberMaxValue(executionCycleWay: FrequenciesE, formItemIndex: number): void {
        const maxValue = this.numberMaxValue(executionCycleWay);
        const numberControl = this.executionCycleCtrs.controls[formItemIndex];

        if (numberControl.value.number > maxValue) {
            numberControl.get('number').setValue(maxValue);
        }
    }
}
